/**
 * @file businessRulesYupExtension.ts
 * Yup extension for business rules validation
 */

import * as Yup from 'yup';
import { LocalizedBusinessRulesEngine } from './businessRulesEngine';
import type {
  LocalizedValidationContext,
  BusinessRuleOperation,
  BusinessRuleValidationResult,
} from 'types/businessRules';

// Extend Yup with business rule validation
Yup.addMethod(Yup.object, 'businessRules', function (
  schema: any,
  operation: BusinessRuleOperation,
  context: Omit<LocalizedValidationContext, 'data'>
) {
  return this.test('business-rules', 'Business rule validation failed', async function (value) {
    const { createError } = this;

    // Skip if no business rules defined
    if (!schema.businessRules?.length && !schema.flow?.businessRules?.length) {
      return true;
    }

    const rulesEngine = new LocalizedBusinessRulesEngine();
    const validationContext: LocalizedValidationContext = {
      ...context,
      operation,
      data: value || {}
    };

    let result: BusinessRuleValidationResult;
    try {
      result = await rulesEngine.validateBusinessRules(validationContext, schema);
    } catch (error: unknown) {
      // A rule that throws is a server-side fault, not the caller's input: rethrown rather than
      // answered as a validation failure, so the write is refused as a 500, never a 400.
      context.fastify?.log.error({ err: error }, '[BusinessRules] rule evaluation threw');
      throw error;
    }

    if (!result.valid) {
      // Create structured error with all violations
      const errorMessages = result.errors.map(e => `${e.ruleName}: ${e.message}`);
      const warningMessages = result.warnings.map(w => `${w.ruleName}: ${w.message}`);

      let fullMessage = errorMessages.join('; ');
      if (warningMessages.length > 0) {
        fullMessage += warningMessages.length > 0 ? ` (Warnings: ${warningMessages.join('; ')})` : '';
      }

      return createError({
        message: fullMessage,
        path: this.path,
        params: {
          businessRuleErrors: result.errors,
          businessRuleWarnings: result.warnings
        }
      });
    }

    // Log warnings even if validation passes. A rule message can quote property values, so the
    // log names the rules only.
    if (result.warnings.length > 0) {
      context.fastify?.log.warn(
        { ruleIds: result.warnings.map(w => w.ruleId), count: result.warnings.length },
        '[BusinessRules] Validation warnings'
      );
    }

    return true;
  });
});

/**
 * Helper function to create a validation context for business rules
 */
export function createBusinessRulesValidationContext(
  operation: BusinessRuleOperation,
  locale: string,
  t: (key: string, params?: Record<string, any>) => string,
  fastify?: any,
  schemaLocalizations?: any
): Omit<LocalizedValidationContext, 'data'> {
  return {
    type: 'sphere',
    operation,
    locale,
    t,
    fastify,
    schemaLocalizations
  };
}

/**
 * Add business rules validation to a Yup schema
 */
export function addBusinessRulesValidation(
  yupSchema: Yup.ObjectSchema<any>,
  schema: any,
  operation: BusinessRuleOperation,
  context: Omit<LocalizedValidationContext, 'data'>
): Yup.ObjectSchema<any> {
  // Use type assertion to call the businessRules method
  return (yupSchema as any).businessRules(schema, operation, context);
}

export { LocalizedBusinessRulesEngine } from './businessRulesEngine';